import type { ReactNode } from 'react';
import { UserButton, useUser } from '@clerk/react';
import { Card } from '@/components/Card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BusinessSwitcher } from '@/components/BusinessSwitcher';
import {
  AdjustmentsIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  BankIcon,
  ChartIcon,
  QuestionIcon,
  ShieldIcon,
} from '@/components/icons';

interface DashboardCard {
  title: string;
  description: string;
  to: string;
  icon: ReactNode;
}

const cards: DashboardCard[] = [
  {
    title: 'Cash / Bank',
    description: 'Bank accounts, petty cash, transfers, and reconciliation.',
    to: '/cash-bank',
    icon: <BankIcon />,
  },
  {
    title: 'Income / Sales',
    description: 'Record sales, services, and customer receipts.',
    to: '/income',
    icon: <ArrowDownLeftIcon />,
  },
  {
    title: 'Expense / Payments',
    description: 'Rent, utilities, fuel, supplies, and other expenses.',
    to: '/expense',
    icon: <ArrowUpRightIcon />,
  },
  {
    title: 'General / Adjustments',
    description: 'Depreciation, corrections, owner drawings, and accruals.',
    to: '/general',
    icon: <AdjustmentsIcon />,
  },
  {
    title: 'Tax / Compliance',
    description: 'Sales Tax, Goods Tax, Withholding Tax — track and remit.',
    to: '/tax',
    icon: <ShieldIcon />,
  },
  {
    title: 'Reports',
    description: 'Trial Balance, Profit & Loss, Balance Sheet, General Ledger.',
    to: '/reports',
    icon: <ChartIcon />,
  },
];

export function Dashboard() {
  const { user } = useUser();
  const greeting = user?.firstName ? `Welcome back, ${user.firstName}` : 'Welcome back';

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">WanEntry</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">{greeting}</p>
        </div>
        <div className="flex items-center gap-2">
          <BusinessSwitcher />
          <button
            type="button"
            aria-label="Help"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <QuestionIcon />
          </button>
          <ThemeToggle />
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: {
                avatarBox: 'h-9 w-9',
              },
            }}
          />
        </div>
      </header>

      <main>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <Card
              key={card.to}
              title={card.title}
              description={card.description}
              to={card.to}
              icon={card.icon}
            />
          ))}
        </div>
      </main>

      <footer className="mt-12 text-center text-xs text-slate-500 dark:text-slate-500">
        WanEntry v0.1.0 — Pre-alpha
      </footer>
    </div>
  );
}
